import React from "react";
import { toast } from "react-toastify";
import { Download } from "@mui/icons-material";
import { Button } from "@mui/material";
import moment from "moment";

import { useLazyGetTicketListQuery } from "store/services/ticketApi";
import { exportExcelFile } from "utils/exportExcelFile";

const ExportTicketExcel = ({ filters = {}, disabled = false }) => {
  const [isExporting, setIsExporting] = React.useState(false);
  const [fetchTickets] = useLazyGetTicketListQuery();

  const handleDownload = async () => {
    try {
      setIsExporting(true);

      const response = await fetchTickets({
        download: true,
        active: filters.active,
        ...(filters.stage && { stage: filters.stage }),
        ...(filters.keyword && { keyword: filters.keyword }),
        ...(filters.responsible && { responsible: filters.responsible }),
        ...(filters.vendorId && { vendorId: filters.vendorId }),
        ...(filters.ticketType && filters.ticketType !== "All" && {
          ticketType: filters.ticketType,
        }),
        ...(filters.fromDate && { fromDate: filters.fromDate }),
        ...(filters.toDate && { toDate: filters.toDate }),
      }).unwrap();

      const tickets = response?.data || [];
      if (!tickets.length) {
        toast.info("No ticket records found for the selected filters.");
        return;
      }

      const stripHtmlTags = (html) =>
        html ? html.replace(/<[^>]*>?/gm, "").trim() : "N/A";

      const formattedData = tickets.map((row, index) => {
        // VendorProfile.bankAccount is the source of truth. Match the
        // ticket's selected account first; fall back to the ticket snapshot
        // for older records whose account was not persisted to VendorProfile.
        const ticketBankDetails = row.bankDetails || {};
        const ticketAccountNumber = String(
          ticketBankDetails.bankAccountNumber || "",
        ).trim();
        const vendorBankAccounts = Array.isArray(row.sourceRef?.bankAccount)
          ? row.sourceRef.bankAccount
          : [];
        const vendorBankDetails = ticketAccountNumber
          ? vendorBankAccounts.find(
              (account) =>
                String(account.bankAccountNumber || "").trim() ===
                ticketAccountNumber,
            )
          : vendorBankAccounts.find((account) => account.status === "Active") ||
            vendorBankAccounts[0];
        const bankDetails =
          vendorBankDetails ||
          (Object.keys(ticketBankDetails).length
            ? ticketBankDetails
            : row.sourceRef?.vendorProfileBankDetail) ||
          {};

        return {
          SN: index + 1,
          "Ticket ID": row.ticketId || "N/A",
          Subject: row.subject || "N/A",
          Vendor: row.sourceRef?.shopName || row.sourceRef?.ownerName || "N/A",
          "Ticket Type": row.ticketType?.name || "N/A",
          Description: stripHtmlTags(row.description),
          "Bank Name": bankDetails.bankName || "-",
          "Account Name": bankDetails.bankAccountName || "-",
          "Account Number": bankDetails.bankAccountNumber || "-",
          Address: row.address || row.sourceRef?.address || "-",
          "Created Date": row.createdAt
            ? moment(row.createdAt).format("YYYY-MM-DD")
            : "N/A",
        };
      });

      exportExcelFile({ sheetData: formattedData, fileName: "Ticket_List" });
      toast.success("Ticket data downloaded successfully");
    } catch (error) {
      console.error("Error fetching ticket data:", error);
      toast.error(error?.data?.message || "Error downloading ticket data");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      startIcon={<Download />}
      variant="outlined"
      size="small"
      onClick={handleDownload}
      disabled={disabled || isExporting}
    >
      {isExporting ? "Exporting..." : "Export"}
    </Button>
  );
};

export default ExportTicketExcel;
